import { useGetRiskScoresV1RiskScoresGet } from '@/api/generated-catch-backend/risk-scores/risk-scores';
import { useGetTestSuggestionsV1TestSuggestionsGet } from '@/api/generated-catch-backend/test-suggestions/test-suggestions';
import { useGetActionPlanInterventionsV1ActionPlanInterventionsGet } from '@/api/generated-catch-backend/action-plan-interventions/action-plan-interventions';
import { InterventionStatus, TestSuggestionStatus } from '@/api/generated-catch-backend/model';
import {
  useRiskFactorsByCancerControllerGetUserRisk,
  useUserRiskControllerGetUserRisk,
} from '@/api/generated/default/default';
import { TypeOfCancer } from '@/components/icons/icon-catch/types';
import { RISK_LOWER_THRESHOLD, RISK_UPPER_THRESHOLD } from '@/constants/risks';
import { formatCancerTypeNameWithCapitalization } from '@/utils';
import { risksByCancerToSorted } from '@/utils/risks/sort-cancer-risks';
import { useGetLabOrdersV0LabsOrdersGet } from '@/api/generated-catch-backend/labs/labs';
export enum RiskLevel {
  Above = 'above',
  Below = 'below',
  Average = 'average',
}

export default function useDashboardBodyController() {
  /**** Risks ****/

  const { data: riskData } = useUserRiskControllerGetUserRisk();
  const { data: riskScoresData } = useGetRiskScoresV1RiskScoresGet();
  const { data: testSuggestionData } = useGetTestSuggestionsV1TestSuggestionsGet({
    status: [TestSuggestionStatus.surfaced]
  });
  const {data: recommendedActionsData } = useGetActionPlanInterventionsV1ActionPlanInterventionsGet({
    status: [InterventionStatus.AVAILABLE, InterventionStatus.IN_PROGRESS]
  });

  const riskDataByCancer = riskData?.risk.risks ?? [];

  type ProcessedRiskData = Record<TypeOfCancer, { risk: number; level: RiskLevel }>;

  const processedRiskDataByCancer = riskDataByCancer.reduce((acc, { cancer, risk, averageRisk, potentialRisk }) => {
    acc[cancer] = {
      risk: risk,
      level: _getRiskLevel(risk, averageRisk),
    };
    return acc;
  }, {} as ProcessedRiskData);

  /*** Risk Factors ***/

  const { data: riskFactorsByCancer } = useRiskFactorsByCancerControllerGetUserRisk();
  const sortedRiskFactorsByCancer = risksByCancerToSorted(riskFactorsByCancer);

  const risks = sortedRiskFactorsByCancer?.factorsPerCancer.map(({ cancerType, riskFactorsSummary }) => ({
    cancerId: cancerType,
    name: formatCancerTypeNameWithCapitalization(cancerType),
    level: processedRiskDataByCancer?.[cancerType]?.level ?? RiskLevel.Average,
    risk:
      cancerType === 'overall'
        ? (riskData?.risk.overallRisk ?? 0)
        : (processedRiskDataByCancer?.[cancerType]?.risk ?? 0),
    /**
     * The positiveImpactRiskFactorsCount negatively affects the person,
     * represents a positive number risk and so, increases the overall risk.
     *
     * The negativeImpactRiskFactorsCount is beneficial for the person,
     * represents a negative number risk and so decreases the overall risk.
     */
    increasing: riskFactorsSummary.positiveImpactRiskFactorsCount,
    decreasing: riskFactorsSummary.negativeImpactRiskFactorsCount,
  }));

  const cancersCount = risks?.reduce<{
    cancersAbove: number;
    cancersBelow: number;
    cancersAverage: number;
  }>(
    (acc, { level }) => {
      if (level === RiskLevel.Above) {
        return { ...acc, cancersAbove: acc.cancersAbove + 1 };
      } else if (level === RiskLevel.Below) {
        return { ...acc, cancersBelow: acc.cancersBelow + 1 };
      } else {
        return { ...acc, cancersAverage: acc.cancersAverage + 1 };
      }
    },
    { cancersAbove: 0, cancersBelow: 0, cancersAverage: 0 },
  );

  /*** Step Size ***/

  const maxFactorsCount = Math.max(...(risks?.map(({ increasing, decreasing }) => increasing + decreasing) ?? []));

  risks?.sort((a, b) => a.cancerId.localeCompare(b.cancerId));

  const stepSize = 100 / maxFactorsCount;

  /*** Lab Orders ***/

  const { data: ordersData } = useGetLabOrdersV0LabsOrdersGet({
    size: 1
  });
  const orders = ordersData?.orders || [];
  const latestOrder = orders[0];

  // Check if lab results are complete
  const hasCompleteLabResults = latestOrder?.status?.toLowerCase().includes('complete');

  // TODO: Replace with actual biomarker counts from API
  // For now using placeholder values
  const biomarkerCounts = {
    optimal: 32,
    normal: 12,
    outOfRange: 6,
  };

  return {
    risks,
    riskScoresData,
    testSuggestionData,
    recommendedActionsData,
    cancersAbove: cancersCount?.cancersAbove ?? 0,
    cancersBelow: cancersCount?.cancersBelow ?? 0,
    cancersAverage: cancersCount?.cancersAverage ?? 0,
    stepSize,
    hasCompleteLabResults,
    biomarkerCounts,
  };
}

function _getRiskLevel(risk: number, averageRisk: number): RiskLevel {
  if (risk > averageRisk * RISK_UPPER_THRESHOLD) {
    return RiskLevel.Above;
  } else if (risk < averageRisk * RISK_LOWER_THRESHOLD) {
    return RiskLevel.Below;
  } else {
    return RiskLevel.Average;
  }
}
